import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Container, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState, resetAllStores, store } from '../../../store';
import { useGenericMutation } from '../../../services/react-query/generic.mutation';
import { queryClient } from '../../../services/react-query/react-query.client';
import { AuthService } from '../../../services/auth.service';
import { publishSessionSyncEvent, SESSION_SYNC_ACTIONS } from '../../../services/session-sync.service';
import { KnowledgeBasePanel } from './knowledge-base-panel';
import { InstructionsPanel } from './instructions-panel';
import { ActivityPanel } from './activity-panel';
import { ChatsPanel } from './chats-panel';
import { SettingsPanel } from './settings-panel';

type TabKey = 'kb' | 'instructions' | 'activity' | 'chats' | 'settings';

// Stable slugs (not indexes) so bookmarked/shared URLs survive a future
// tab reorder. Keep in sync with the `value`s on the <Tab> elements below.
const TAB_KEYS: TabKey[] = ['kb', 'instructions', 'activity', 'chats', 'settings'];
// Chats (Michael, 10/23): it is where support works all day.
const DEFAULT_TAB: TabKey = 'chats';

function isTabKey(value: string | null): value is TabKey {
  return value != null && (TAB_KEYS as string[]).includes(value);
}

/**
 * POM Master "Support Agent" tab (design doc §6.7) — manages the Zendesk
 * draft-copilot's knowledge base, instructions, and activity/verdicts. The
 * backing store (`support_agent` Mongo database) is a deliberate singleton
 * (§6.8): this same tab, on dev, QA, or prod, reads and writes the one live
 * KB/instructions/activity log the bot actually uses. There is no "safe to
 * experiment" copy — every save here is a save to the live bot.
 */
export const SupportAgentScreen: React.FC = () => {
  const authUser = useSelector((s: RootState) => (s as any).auth?.authUser) as any;

  // The URL is the single source of truth for the active tab (so a refresh
  // or a shared link lands back on the same tab) — `tab` is derived from
  // `?tab=` on every render and `setTab` writes it back with `replace`
  // (not `push`) so switching tabs doesn't pile up history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // A chat= deep link implies the Chats tab regardless of tab= (Pollen
  // review, PR #540, low item) — a link that only set `chat=` and forgot
  // `tab=chats` must not silently land on Knowledge Base with the chat
  // param inert. `!!...` not `!== null` (Pollen review, round 5, item 2):
  // an EMPTY `?chat=` (no id at all) is falsy-but-present — `!== null`
  // still counted it, locking every tab to Chats with nothing to show.
  const hasChatParam = !!searchParams.get('chat');
  const tab: TabKey = hasChatParam ? 'chats' : isTabKey(tabParam) ? tabParam : DEFAULT_TAB;
  const awayChatRef = useRef<string | null>(null);
  // Chats stays mounted once shown, hidden on the other tabs (attack/defend
  // round 2, 10/26): leaving it no longer drops the open chat, a draft, or an
  // in-flight reply or label and its error.
  // A ref, not state: marking it costs no extra render.
  const chatsShownRef = useRef(false);
  if (tab === 'chats') chatsShownRef.current = true;
  const setTab = useCallback(
    (nextTab: TabKey) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', nextTab);
      // An article link (?kbDoc=) is for one visit to the KB tab, never a
      // tab switch later (attack/defend round 3).
      nextParams.delete('kbDoc');
      // Likewise ?chat= (the open chat) only holds on Chats: left in place it
      // kept every other tab from opening (attack/defend 10/26). It comes
      // back on the way to Chats, where that chat is still open.
      const chatParam = nextParams.get('chat');
      if (nextTab !== 'chats') {
        if (chatParam) awayChatRef.current = chatParam;
        nextParams.delete('chat');
      } else if (!chatParam && awayChatRef.current) {
        nextParams.set('chat', awayChatRef.current);
        awayChatRef.current = null;
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Masquerading (locked brief, item C): a masqueraded session's context is
  // the customer's, not the POM Master's — stop it first, then let this
  // page render normally once `authUser.masquarade` clears (same sequence
  // sidebar.tsx's own "stop masquerade" button runs).
  const { mutateAsync: stopMasquarade } = useGenericMutation<null, boolean>(
    'stopMasquaradeUser',
    'customerWebAppUser/stopMasquarade',
    'post',
  );
  const [stopMasquaradeError, setStopMasquaradeError] = useState<string | null>(null);
  useEffect(() => {
    if (!authUser?.masquarade) return;
    (async () => {
      try {
        await stopMasquarade(null);
        // Same store/cache reset the masquerade-exit button uses (sidebar.tsx)
        // — a subscription-scoped store must not keep serving the masqueraded
        // customer's data once we're back to the POM Master's own context.
        resetAllStores();
        queryClient.clear();
        await AuthService.whoIam();
        // Neither call above throws on a server-side `isValid: false`
        // (Pollen review, round 5, item 1 — "the masquerade spinner can
        // still hang"): `useGenericMutation` resolves normally either way,
        // and `whoIam()` just returns quietly without updating the store.
        // Reading the store directly (not the stale `authUser` this closure
        // captured) is the only way to tell "it worked" from "it silently
        // didn't" — a re-render alone can't be trusted to have happened.
        if (store.getState().auth?.authUser?.masquarade) {
          setStopMasquaradeError("Couldn't leave masquerade mode — try reloading the page.");
          return;
        }
        publishSessionSyncEvent(SESSION_SYNC_ACTIONS.MASQUERADE_CONTEXT_CHANGED);
      } catch {
        // Pollen review (PR #540, medium item 3): without this, a rejected
        // stopMasquarade/whoIam call left the "Leaving masquerade mode…"
        // spinner spinning forever, with no way out short of a full reload.
        setStopMasquaradeError("Couldn't leave masquerade mode — try reloading the page.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.masquarade]);

  // Masquerade is checked FIRST (Pollen review, PR #540, medium item 3): a
  // masquerading session still carries the underlying POM Master's own
  // `pomMaster: true` (see ChatActor's shape elsewhere in this app), so
  // checking `!pomMaster` first would show a masquerading POM Master the
  // wrong message ("for POM Master users") instead of the masquerade-exit
  // state they're actually in.
  // The app layout keeps room for a header taller than the one it draws
  // (10vh + 24px from lg, for a ~52px bar), so every page starts with a
  // band of blank space. This page takes it back (Michael, 10/23: "what is
  // all that useless whitespace at the top of the page?"): it starts 8px
  // under the header bar, measured.
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [pullUp, setPullUp] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      const page = pageRef.current;
      const main = page?.closest('main');
      const header = document.querySelector('header');
      if (!page || !main || !header) return;
      const mainPaddingTop = parseFloat(window.getComputedStyle(main).paddingTop) || 0;
      const next = Math.max(0, Math.round(mainPaddingTop - header.getBoundingClientRect().bottom - 8));
      setPullUp((current) => (current === next ? current : next));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  if (authUser?.masquarade) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={2}>
          {stopMasquaradeError ? (
            <Typography variant="body2" color="error">
              {stopMasquaradeError}
            </Typography>
          ) : (
            <>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Leaving masquerade mode…
              </Typography>
            </>
          )}
        </Stack>
      </Container>
    );
  }

  // Not a POM Master (locked brief, item C): render a short message rather
  // than silently redirecting away — this link was sent because someone
  // expected it to open a specific chat, so a bare bounce to '/' would look
  // like the link (or their account) is broken.
  if (!authUser?.pomMaster) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6">This link is for POM Master users.</Typography>
      </Container>
    );
  }

  return (
    <Container ref={pageRef} maxWidth="xl" sx={{ pt: 1, pb: 3, mt: `-${pullUp}px` }}>
      {/* Title and tabs share one row (Michael, 10/23: "push everything further up"). */}
      <Stack direction="row" alignItems="center" columnGap={4} sx={{ flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="h4" sx={{ fontSize: 22, whiteSpace: 'nowrap' }}>
          Support Agent
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 1 } }}
        >
          {/* Chats first (Michael, 10/22): it is where support works all day. */}
          <Tab value="chats" label="Chats" />
          <Tab value="kb" label="Knowledge Base" />
          <Tab value="instructions" label="Instructions" />
          <Tab value="activity" label="Activity" />
          <Tab value="settings" label="Settings" />
        </Tabs>
      </Stack>
      {/* About the bot's settings, not chats — the Chats tab gives the room to the inbox (split view design). */}
      {tab !== 'chats' && (
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Manage the Zendesk draft-copilot: knowledge base, instructions, and activity. This is a singleton store
          shared by dev, QA, and prod — every save here changes the live bot.
        </Typography>
      )}
      <Box>
        {tab === 'kb' && (
          <KnowledgeBasePanel
            initialDocId={searchParams.get('kbDoc') || undefined}
            onLeaveInitialDoc={() => {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.delete('kbDoc');
              setSearchParams(nextParams, { replace: true });
            }}
          />
        )}
        {tab === 'instructions' && <InstructionsPanel />}
        {tab === 'activity' && <ActivityPanel />}
        {chatsShownRef.current && (
          <Box sx={{ display: tab === 'chats' ? 'block' : 'none' }}>
            <ChatsPanel hidden={tab !== 'chats'} />
          </Box>
        )}
        {tab === 'settings' && <SettingsPanel />}
      </Box>
    </Container>
  );
};

export default SupportAgentScreen;
